import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [demoMode, setDemoMode] = useState(params.get("demo") === "1");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { email, code });
      setVerified(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e?.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr("");
    try {
      const { data } = await api.post("/auth/resend-otp", { email });
      setDemoMode(!!data.demo_mode);
      setCooldown(60);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e?.message);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
        <div className="w-full max-w-sm text-center space-y-5" data-testid="verify-success">
          <CheckCircle size={56} weight="duotone" className="text-emerald-500 mx-auto" />
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold">Email verified successfully.</h2>
          <p className="text-sm text-zinc-500">Your account is now active. Sign in to enter WorkMesh.</p>
          <Button onClick={() => navigate("/login")} className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" data-testid="continue-to-login-button">
            Continue to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
      <form className="w-full max-w-sm space-y-5" onSubmit={submit} data-testid="verify-email-form">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">One more step</div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold">Verify your email</h2>
          <p className="text-sm text-zinc-500 mt-2">
            We&apos;ve sent a 6-digit verification code to your email address.
          </p>
        </div>

        {demoMode && (
          <div className="text-xs text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-sm" data-testid="verify-demo-notice">
            Demo environment: outgoing email is not configured yet, so verification codes are held in the demo outbox.
            Use the prefilled demo accounts on the login page to explore WorkMesh instantly.
          </div>
        )}

        {err && (
          <div className="text-sm text-red-600 border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-sm" data-testid="verify-error">
            {err}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Email</Label>
          <Input data-testid="verify-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Verification code</Label>
          <Input
            data-testid="verify-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="______"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
            className="rounded-sm text-center text-2xl tracking-[0.5em] font-mono h-14"
          />
        </div>
        <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" data-testid="verify-submit-button">
          {busy ? "Verifying…" : "Verify Email"}
        </Button>

        <div className="text-sm text-zinc-500 flex items-center justify-between">
          <span>Didn&apos;t receive the code?</span>
          <button
            type="button"
            onClick={resend}
            disabled={cooldown > 0 || !email}
            className="underline underline-offset-4 text-foreground disabled:opacity-50 disabled:no-underline"
            data-testid="resend-otp-button"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Verification Code"}
          </button>
        </div>

        <p className="text-sm text-zinc-500">
          <Link to="/login" className="underline underline-offset-4 text-foreground" data-testid="verify-back-to-login">
            Back to Login
          </Link>
        </p>
      </form>
    </div>
  );
}
